/**
 * @fileoverview Authentication Wrapper Component
 * @active Used in auth layouts to provide consistent styling and slideshow for authentication pages
 *
 * This component wraps authentication forms with a consistent layout including
 * a slideshow on the right side and the auth form on the left.
 *
 * @see {@link /src/layouts/auth/FormLayout.tsx} - Form layout component used inside this wrapper
 * @see {@link /src/app/(main)/(auth)/layout.tsx} - App router layout that uses this wrapper
 * @see {@link /src/components/auth/SignIn.tsx} - SignIn component rendered within this wrapper
 * @see {@link /src/components/auth/SignUp.tsx} - SignUp component rendered within this wrapper
 */

"use client";

import { type ReactNode, useState, useEffect } from "react";
import { Flex, Grid, Text } from "@chakra-ui/react";
import Link from "next/link";
import dynamic from "next/dynamic";

// Import CSS statically - these need to be included regardless of dynamic imports
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import "swiper/css/scrollbar";

// Import SVGs
import Slide1 from "@/assets/svg/auth/Slide1.svg";
import Slide2 from "@/assets/svg/auth/Slide2.svg";
import Slide3 from "@/assets/svg/auth/Slide3.svg";

// Dynamic imports with proper typing and no-SSR flag
const DynamicSwiper = dynamic(
  () => import("swiper/react").then((mod) => mod.Swiper),
  { ssr: false }
);
const DynamicSwiperSlide = dynamic(
  () => import("swiper/react").then((mod) => mod.SwiperSlide),
  { ssr: false }
);

/**
 * Breakpoints for responsive swiper
 */
const breakpoints = {
  420: {
    slidesPerView: 1,
    spaceBetween: 20,
    centeredSlides: true,
  },
  768: {
    slidesPerView: 1,
    spaceBetween: 20,
    centeredSlides: true,
  },
  1024: {
    slidesPerView: 1,
    spaceBetween: 20,
    centeredSlides: true,
  },
} as const;

/**
 * Footer links for the auth pages
 */
const authFooterLink = [
  { id: 1, path: "/contact", name: "Support" },
  { id: 2, path: "/privacy", name: "Privacy" },
] as const;

/**
 * Props for the SliderText component
 */
interface SliderTextProps {
  /** Title text to display */
  title: string;
  /** Bottom margin (optional) */
  mb?: string;
}

/**
 * Text component for slider titles
 *
 * @param {SliderTextProps} props - The component props
 * @returns {JSX.Element} The rendered slider text component
 */
const SliderText = ({ title, mb }: SliderTextProps): JSX.Element => {
  return (
    <Text
      justifySelf={"center"}
      alignSelf={"center"}
      fontFamily={"Roslindale"}
      fontSize={{ base: "36px", lg: "51px" }}
      maxW={"475px"}
      h={{ base: "unset", md: "70px", lg: "102px" }}
      color={"white"}
      textAlign={"center"}
      lineHeight={"1"}
      fontStyle={"normal"}
      fontWeight={300}
      pt={"4px"}
      mb={mb ? mb : "12px"}
    >
      {title}
    </Text>
  );
};

/**
 * Props for the AuthWrapper component
 */
interface AuthWrapperProps {
  /** Child components (typically auth forms) */
  children: ReactNode;
}

/**
 * Authentication wrapper component
 * Provides a consistent layout for all authentication pages with
 * a slideshow on the right side and the auth form on the left
 *
 * @param {AuthWrapperProps} props - Component props
 * @returns {JSX.Element} The rendered auth wrapper
 */
const AuthWrapper = ({ children }: AuthWrapperProps): JSX.Element => {
  // State for Swiper modules
  const [swiperModules, setSwiperModules] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Load Swiper modules dynamically
  useEffect(() => {
    const loadModules = async () => {
      try {
        // Import modules directly for better type safety and to avoid undefined errors
        const modules = await Promise.all([
          import("swiper/modules").then((mod) => mod.Pagination),
          import("swiper/modules").then((mod) => mod.Autoplay),
        ]);

        setSwiperModules(modules);
        setIsLoading(false);
      } catch (error) {
        console.error("Failed to load Swiper modules:", error);
        // Set loading to false even on error to show the fallback UI
        setIsLoading(false);
      }
    };

    loadModules();
  }, []);

  // Early return while modules are loading
  if (isLoading || swiperModules.length !== 2) {
    return (
      <Grid
        templateColumns={{ base: "1fr", md: "repeat(2, minmax(0, 1fr))" }}
        h={"100vh"}
        maxH={"100vh"}
      >
        <Flex
          flexDir={"column"}
          alignItems={"center"}
          pb={"10px"}
          bg={{ base: "mainBlue", md: "transparent" }}
        >
          <Flex flexGrow={"1"} mt={"24px"} alignItems={"center"}>
            {children}
          </Flex>
        </Flex>
        <Grid
          display={{ base: "none", md: "grid" }}
          alignItems="center"
          justifyContent="center"
          bg="mainBlue"
        >
          <Text color="white">Loading slideshow...</Text>
        </Grid>
      </Grid>
    );
  }

  // Destructure modules for clarity
  const [Pagination, Autoplay] = swiperModules;

  return (
    <Grid
      templateColumns={{ base: "1fr", md: "repeat(2, minmax(0, 1fr))" }}
      h={"100vh"}
      maxH={"100vh"}
    >
      <Flex
        flexDir={"column"}
        alignItems={"center"}
        pb={"10px"}
        bg={{ base: "mainBlue", md: "transparent" }}
      >
        <Flex flexGrow={"1"} mt={"24px"} alignItems={"center"}>
          {children}
        </Flex>
        <Flex
          flexDir={{ base: "column", lg: "row" }}
          alignItems={"center"}
          justifySelf={"end"}
          columnGap={"18px"}
          color={{ base: "white", md: "black" }}
        >
          <Text
            fontSize={"14px"}
            letterSpacing={"0.28px"}
            lineHeight={"normal"}
          >
            Copyright 2025, aVenture Investment Company
          </Text>
          <nav>
            <Flex alignItems={"center"} columnGap={"9px"}>
              {authFooterLink.map((item) => (
                <Link key={item.id} href={item.path} title={item.name}>
                  <Text
                    textDecoration={"underline"}
                    fontSize={"14px"}
                    letterSpacing={"0.28px"}
                    lineHeight={"normal"}
                    cursor={"pointer"}
                  >
                    {item.name}
                  </Text>
                </Link>
              ))}
            </Flex>
          </nav>
        </Flex>
      </Flex>
      <Grid
        maxH={"100vh"}
        display={{ base: "none", md: "grid" }}
        justifyContent={"center"}
        bg={"mainBlue"}
        pt={"63px"}
        pb={"10px"}
        px={"24px"}
        alignItems={"center"}
        h={"100vh"}
      >
        <Flex
          minH={"100%"}
          justifySelf={"center"}
          maxW={"calc(100vw / 2 - 48px)"}
          justifyContent={"center"}
          pos={"relative"}
          alignSelf={"center"}
        >
          <DynamicSwiper
            className="swiperAuth"
            breakpoints={breakpoints}
            modules={[Pagination, Autoplay]}
            initialSlide={0}
            pagination={{ clickable: true }}
            autoplay={{
              delay: 5500,
              disableOnInteraction: false,
            }}
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              width: "100%",
              minWidth: "100%",
              maxHeight: "100vh",
            }}
          >
            <DynamicSwiperSlide>
              <Flex w="100%" h="100%" align="center" justify="center" pb="42px">
                <Flex flexDir={"column"} pos={"relative"} minW={"100%"}>
                  <SliderText
                    title={"Explore companies and funds"}
                    mb={"50px"}
                  />
                  <Flex
                    maxW={{
                      base: "calc(100vw / 2)",
                    }}
                    maxH={{
                      base: "calc(100vh - 225px)",
                      lg: "calc(100vh - 257px)",
                    }}
                    alignSelf={"center"}
                  >
                    <Slide1 />
                  </Flex>
                </Flex>
              </Flex>
            </DynamicSwiperSlide>
            <DynamicSwiperSlide>
              <Flex w="100%" h="100%" align="center" justify="center" pb="42px">
                <Flex flexDir={"column"} pos={"relative"} minW={"100%"}>
                  <SliderText
                    title={"Make decisions based on analysis"}
                    mb={"50px"}
                  />
                  <Flex
                    maxW={{
                      base: "calc(100vw / 2)",
                    }}
                    maxH={{
                      base: "calc(100vh - 225px)",
                      lg: "calc(100vh - 257px)",
                    }}
                    alignSelf={"center"}
                  >
                    <Slide2 />
                  </Flex>
                </Flex>
              </Flex>
            </DynamicSwiperSlide>
            <DynamicSwiperSlide>
              <Flex w="100%" h="100%" align="center" justify="center" pb="42px">
                <Flex flexDir={"column"} pos={"relative"} minW={"100%"}>
                  <SliderText title={"Analyze Portfolios"} mb={"50px"} />
                  <Flex
                    maxW={{
                      base: "calc(100vw / 2)",
                    }}
                    maxH={{
                      base: "calc(100vh - 225px)",
                      lg: "calc(100vh - 257px)",
                    }}
                    alignSelf={"center"}
                  >
                    <Slide3 />
                  </Flex>
                </Flex>
              </Flex>
            </DynamicSwiperSlide>
          </DynamicSwiper>
        </Flex>
      </Grid>
    </Grid>
  );
};

export default AuthWrapper;
